import React, { useRef, useState, useEffect, useMemo } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { getAllProducts, getCategories, Product } from '../utils/productManager';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const Products: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>(['All']);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [fetchedProducts, fetchedCategories] = await Promise.all([
          getAllProducts(),
          getCategories()
        ]);

        if (!ignore) {
          setProducts(fetchedProducts);
          const categoryNames = ['All', ...fetchedCategories.map(c => c.name)];
          setCategories(categoryNames);
        }
      } catch (err) {
        console.error("Failed to fetch products", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, []);

  // Filter products directly from state
  const filteredProducts = useMemo(() => {
    return selectedCategory === 'All'
      ? products
      : products.filter(p => p.category === selectedCategory);
  }, [products, selectedCategory]);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Header Animation
    if (headerRef.current) {
      gsap.fromTo(headerRef.current,
        {
          y: 30,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out'
        }
      );
    }
  }, { scope: containerRef }); // Run once

  // Animate grid items when category changes
  useGSAP(() => {
    if (filteredProducts.length > 0) {
      // Kill old animations to prevent conflict
      gsap.killTweensOf('.product-item');

      gsap.fromTo('.product-item',
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.05,
          ease: 'power2.out',
          overwrite: 'auto'
        }
      );
    }
  }, { scope: containerRef, dependencies: [filteredProducts] });

  return (
    <div ref={containerRef} className="min-h-screen bg-[#fcfcfb] pt-32 pb-24 px-4 md:px-8">
      <SEO
        title="Luxury Furniture Collection"
        description="Explore our exclusive collection of handcrafted luxury furniture, featuring bespoke designs for every room in your home."
        url="/products"
      />

      {/* Page Header */}
      <div ref={headerRef} className="max-w-7xl mx-auto text-center mb-12 opacity-0">
        <span className="text-[#3A4B2C] uppercase tracking-[0.4em] text-xs font-bold font-sans block mb-4">
          Masterpieces
        </span>
        <h1 className="text-5xl md:text-7xl font-serif text-gray-900 mb-6">
          Our Collection
        </h1>
        <p className="max-w-2xl mx-auto text-gray-600 font-sans leading-relaxed text-sm md:text-base">
          Handcrafted excellence for the discerning few.
        </p>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto mb-16 overflow-x-auto pb-4">
        {loading ? (
          <div className="text-center text-gray-400">Loading categories...</div>
        ) : (
          <div className="flex justify-center flex-wrap gap-4 min-w-max px-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`
                px-8 py-3 rounded-full text-sm uppercase tracking-widest font-sans font-medium transition-all duration-300
                ${selectedCategory === category
                    ? 'bg-gray-900 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-900 hover:text-gray-900'}
              `}
              >
                {category}
              </button>
            ))}
          </div>
        )}
      </div>


      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-xl text-gray-400 font-serif animate-pulse">Loading Collection...</div>
        </div>
      ) : (
        <div className="product-grid max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {filteredProducts.map((product) => (
            <Link
              to={`/products/${product.id}`}
              key={product.id}
              className="product-item group block cursor-pointer opacity-0" // Start invisible to prevent GSAP flicker
            >
              {/* Image Container */}
              <div className="relative aspect-square bg-white rounded-[2rem] shadow-sm mb-6 flex items-center justify-center p-8 transition-all duration-500 ease-out group-hover:shadow-xl group-hover:-translate-y-2 overflow-hidden border border-gray-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain transform transition-transform duration-700 ease-in-out group-hover:scale-110"
                />
              </div>

              {/* Product Info - Always Visible */}
              <div className="text-center space-y-2">
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider font-sans">
                  {product.name}
                </h3>
                <p className="text-xs text-gray-500 font-sans tracking-widest">
                  ID: {product.code || `MH${9000 + product.id}`}
                </p>
              </div>
            </Link>
          ))}
          {filteredProducts.length === 0 && (
            <div className="col-span-full text-center text-gray-400 py-10">
              No products found in this category.
            </div>
          )}
        </div>
      )}

      {/* Footer Text */}
      <div className="mt-32 text-center">
        <p className="text-gray-400 text-xs tracking-widest uppercase">
          Displaying {filteredProducts.length} exclusive items
        </p>
      </div>
    </div>
  );
};

export default Products;